import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { TenantProvider, useTenant } from './contexts/TenantContext'
import { Loading } from './components/common/Loading'

// Auth Pages
import { LoginPage } from './pages/auth/LoginPage'
import { SignupPage } from './pages/auth/SignupPage'
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage'
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage'

// Candidate Pages
import { MyPage } from './pages/candidate/MyPage'
import { CourseDetailPage } from './pages/candidate/CourseDetailPage'
import { LessonPage } from './pages/candidate/LessonPage'
import { ExamStartPage } from './pages/candidate/ExamStartPage'
import { ExamAttemptPage } from './pages/candidate/ExamAttemptPage'
import { ExamResultPage } from './pages/candidate/ExamResultPage'
import { CertificatePage } from './pages/candidate/CertificatePage'

// Tenant Admin Pages
import { TenantDashboardPage } from './pages/tenant-admin/TenantDashboardPage'
import { CoursesListPage } from './pages/tenant-admin/CoursesListPage'
import { CourseFormPage } from './pages/tenant-admin/CourseFormPage'
import { LessonsManagePage } from './pages/tenant-admin/LessonsManagePage'
import { PricingManagePage } from './pages/tenant-admin/PricingManagePage'
import { ExamQuestionsPage } from './pages/tenant-admin/ExamQuestionsPage'
import { CandidatesManagePage } from './pages/tenant-admin/CandidatesManagePage'
import { ExamResultsManagePage } from './pages/tenant-admin/ExamResultsManagePage'

// Platform Admin Pages
import { PlatformDashboardPage } from './pages/platform-admin/PlatformDashboardPage'
import { TenantsManagePage } from './pages/platform-admin/TenantsManagePage'
import { PlatformCandidatesPage } from './pages/platform-admin/PlatformCandidatesPage'
import { PaymentsManagePage } from './pages/platform-admin/PaymentsManagePage'

// Enrollment Pages
import { ApplyPage } from './pages/enrollment/ApplyPage'
import { EnrollmentConfirmPage } from './pages/enrollment/EnrollmentConfirmPage'
import { EnrollmentSuccessPage } from './pages/enrollment/EnrollmentSuccessPage'
import { EnrollmentCancelPage } from './pages/enrollment/EnrollmentCancelPage'
import { RetryEnrollmentPage } from './pages/enrollment/RetryEnrollmentPage'

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return <Loading fullScreen message="認証情報を確認中..." />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const { isPlatformAdmin, memberships, loading: tenantLoading } = useTenant()

  if (authLoading || tenantLoading) {
    return <Loading fullScreen message="認証情報を確認中..." />
  }

  if (user) {
    // ロール判定によるリダイレクト先決定
    if (isPlatformAdmin) {
      return <Navigate to="/admin/dashboard" replace />
    }

    // テナント管理者判定
    const isTenantAdmin = memberships.some((m) => m.role_in_tenant === 'tenant_admin')
    if (isTenantAdmin) {
      return <Navigate to="/tenant/dashboard" replace />
    }

    // 候補者（デフォルト）
    return <Navigate to="/mypage" replace />
  }

  return <>{children}</>
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-email"
        element={
          <PublicRoute>
            <VerifyEmailPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      {/* Enrollment Routes */}
      <Route path="/apply" element={<ApplyPage />} />
      <Route
        path="/enrollment/confirm"
        element={
          <PrivateRoute>
            <EnrollmentConfirmPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/enrollment/success"
        element={
          <PrivateRoute>
            <EnrollmentSuccessPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/enrollment/cancel"
        element={
          <PrivateRoute>
            <EnrollmentCancelPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/enrollment/retry/:attemptId"
        element={
          <PrivateRoute>
            <RetryEnrollmentPage />
          </PrivateRoute>
        }
      />

      {/* Private Routes */}
      <Route
        path="/mypage"
        element={
          <PrivateRoute>
            <MyPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/courses/:courseId"
        element={
          <PrivateRoute>
            <CourseDetailPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/courses/:courseId/lessons/:lessonId"
        element={
          <PrivateRoute>
            <LessonPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/exams/:examId/start"
        element={
          <PrivateRoute>
            <ExamStartPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/exams/attempt/:attemptId"
        element={
          <PrivateRoute>
            <ExamAttemptPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/exams/result/:attemptId"
        element={
          <PrivateRoute>
            <ExamResultPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/certificates/:certificateId"
        element={
          <PrivateRoute>
            <CertificatePage />
          </PrivateRoute>
        }
      />

      {/* Tenant Admin Routes */}
      <Route
        path="/tenant/dashboard"
        element={
          <PrivateRoute>
            <TenantDashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tenant/candidates"
        element={
          <PrivateRoute>
            <CandidatesManagePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tenant/results"
        element={
          <PrivateRoute>
            <ExamResultsManagePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tenant/courses"
        element={
          <PrivateRoute>
            <CoursesListPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tenant/courses/new"
        element={
          <PrivateRoute>
            <CourseFormPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tenant/courses/:courseId/edit"
        element={
          <PrivateRoute>
            <CourseFormPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tenant/courses/:courseId/lessons"
        element={
          <PrivateRoute>
            <LessonsManagePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tenant/pricing"
        element={
          <PrivateRoute>
            <PricingManagePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tenant/exams/:examId/questions"
        element={
          <PrivateRoute>
            <ExamQuestionsPage />
          </PrivateRoute>
        }
      />

      {/* Platform Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <PrivateRoute>
            <PlatformDashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/tenants"
        element={
          <PrivateRoute>
            <TenantsManagePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/candidates"
        element={
          <PrivateRoute>
            <PlatformCandidatesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <PrivateRoute>
            <PaymentsManagePage />
          </PrivateRoute>
        }
      />

      {/* Default Route */}
      <Route path="/" element={<Navigate to="/mypage" replace />} />

      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900">404</h1>
              <p className="mt-2 text-gray-600">ページが見つかりません</p>
              <a href="/" className="mt-4 inline-block text-blue-600 hover:text-blue-500">
                ホームへ戻る
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TenantProvider>
          <AppRoutes />
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
